/**
 * SystemBilling — tenant-facing /system/billing page (ADR-019 S6.A).
 *
 * Surfaces:
 * 1. Current plan card with status / trial banner / cancel-scheduled
 *    banner / over-limit downgrade banner.
 * 2. Usage row (worker count vs plan limit).
 * 3. Invoice history with filters + CSV export.
 * 4. Outbound webhooks CRUD (tenant admins set their own integrations).
 *
 * Upgrade flow: clicking [Upgrade plan] opens a plan picker dialog;
 * selection calls /checkout and redirects to the Stripe-hosted
 * checkout. Webhook on success flips the subscription back here
 * (via /system/billing/return); we re-fetch current on mount.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Collapse, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider,
  FormControlLabel, FormGroup, IconButton, Paper,
  Stack, Snackbar, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  DeleteOutlineOutlined, ExpandLess, ExpandMore,
  FileDownloadOutlined, SendOutlined,
  RefreshOutlined, WarningAmberOutlined,
  CloseOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  ApiError,
  organizationApi,
  subscriptionApi,
} from '../../../api';
import type { OrganizationProfile } from '../../../api/organization';
import { downloadBlob } from '../../../utils/downloadFile';
import { translateApiError } from '../../../i18n/translateApiError';
import { openSupportCase } from '../../../components/support/SupportCaseDialog';
import {
  canCancel, canUpdatePaymentMethod, isEnding, planAmount, seatSummary,
  shouldShowCapacityWarnings,
} from './subscriptionLifecycle';
import { billedToBlock } from '../../../components/billing/addressFormat';
import { brandLabel, cardStatus, expiryLabel } from '../../../components/billing/cardOnFile';
import type {
  Invoice, PaymentMethodCard, Plan, Subscription, TenantWebhook, Usage,
} from '../../../api/subscription';
import {
  getPlanDescription,
  getPlanDisplayName,
  isCanonicalPlan,
  sortPlansByTier,
} from '../../../components/billing/planMeta';
import PlanPicker, { BillingInterval } from '../../../components/billing/PlanPicker';
import { ConfirmDialog } from '../../../components/primitives';
import { activeLocale } from '../../../utils/datetime';
import WidgetEmptyState from '../../../components/primitives/WidgetEmptyState';
import { ErrorOutlineOutlined, ReceiptLongOutlined } from '@mui/icons-material';


function statusChipColor(s: string | undefined): 'success' | 'warning' | 'error' | 'info' | 'default' {
  if (s === 'active') return 'success';
  if (s === 'trialing') return 'info';
  if (s === 'past_due') return 'warning';
  if (s === 'suspended' || s === 'cancelled') return 'error';
  return 'default';
}

/** Rows shown before "show all". Eight fills the card without dominating the
 *  page, and covers a normal monthly account's last two quarters. */
const INVOICE_PREVIEW_ROWS = 8;
/** Same idea for the audit trail, which grows faster than invoices do. */
const ACTIVITY_PREVIEW_ROWS = 10;

const SUBSCRIPTION_STATUS_KEYS: Record<string, string> = {
  active: 'system:billing.statuses.active',
  trialing: 'system:billing.statuses.trialing',
  past_due: 'system:billing.statuses.pastDue',
  suspended: 'system:billing.statuses.suspended',
  cancelled: 'system:billing.statuses.cancelled',
};


const INVOICE_STATUS_KEYS: Record<string, string> = {  draft: 'system:billing.invoiceStatuses.draft',
  open: 'system:billing.invoiceStatuses.open',
  paid: 'system:billing.invoiceStatuses.paid',
  past_due: 'system:billing.invoiceStatuses.pastDue',
  void: 'system:billing.invoiceStatuses.void',
  uncollectible: 'system:billing.invoiceStatuses.uncollectible',
  refunded: 'system:billing.invoiceStatuses.refunded',
};

const BILLING_EVENT_KEYS: Record<string, string> = {
  subscription_activated: 'system:billing.event_subscription_activated',
  trial_started: 'system:billing.event_trial_started',
  trial_ending: 'system:billing.event_trial_ending',
  trial_ending_3d: 'system:billing.event_trial_ending',
  trial_ending_1d: 'system:billing.event_trial_ending',
  payment_succeeded: 'system:billing.event_payment_succeeded',
  payment_failed: 'system:billing.event_payment_failed',
  invoice_issued: 'system:billing.event_invoice_issued',
  invoice_paid: 'system:billing.event_payment_succeeded',
  subscription_cancelled: 'system:billing.event_subscription_cancelled',
  cancel_scheduled: 'system:billing.event_cancel_scheduled',
  cancel_undone: 'system:billing.event_cancel_undone',
  plan_changed: 'system:billing.event_plan_changed',
  invoice_refunded: 'system:billing.event_invoice_refunded',
};

const WEBHOOK_EVENT_OPTIONS = [
  'subscription_activated',
  'subscription_cancelled',
  'trial_started',
  'trial_ending_3d',
  'trial_ending_1d',
  'payment_succeeded',
  'payment_failed',
  'invoice_issued',
  'auto_suspended',
  'plan_changed',
] as const;

const WEBHOOK_EVENT_KEYS: Record<(typeof WEBHOOK_EVENT_OPTIONS)[number], string> = {
  subscription_activated: 'system:billing.webhookEvents.subscriptionActivated',
  subscription_cancelled: 'system:billing.webhookEvents.subscriptionCancelled',
  trial_started: 'system:billing.webhookEvents.trialStarted',
  trial_ending_3d: 'system:billing.webhookEvents.trialEnding3d',
  trial_ending_1d: 'system:billing.webhookEvents.trialEnding1d',
  payment_succeeded: 'system:billing.webhookEvents.paymentSucceeded',
  payment_failed: 'system:billing.webhookEvents.paymentFailed',
  invoice_issued: 'system:billing.webhookEvents.invoiceIssued',
  auto_suspended: 'system:billing.webhookEvents.autoSuspended',
  plan_changed: 'system:billing.webhookEvents.planChanged',
};


/**
 * Visual catalog used to be inlined here — it now lives in
 * components/billing/planMeta so the TopNav 'Choose your plan'
 * dialog renders the same product chips + metering rows.
 */


const SystemBilling: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sub, setSub] = useState<Subscription | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  // The resolved "Bill to" identity, served by the API. Never recomputed here:
  // this screen promises what the invoice PDF says, and a second implementation of
  // the inheritance rule is a promise the document can break.
  const [billingPreview, setBillingPreview] = useState<
    NonNullable<OrganizationProfile['billing_preview']> | null
  >(null);
  const [webhooks, setWebhooks] = useState<TenantWebhook[]>([]);
  // The card the subscription will actually be charged. Its own request because
  // answering it costs a round trip to the payment provider; null is the normal answer
  // for the Free plan, a trial before card entry, or a provider that is unreachable.
  const [card, setCard] = useState<PaymentMethodCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  // Both tables on this page grow without bound: invoices accumulate one row per
  // billing period plus one per proration, and billing events grow faster still.
  // Showing everything made the page several screens long on an account only a day
  // old. Preview + expand keeps the default view answering the question the admin
  // came with ("what was I charged, and can I get the PDF") while leaving the full
  // history one click away.
  const [showAllInvoices, setShowAllInvoices] = useState(false);
  // Collapsed by default. This block is an audit trail of event codes and provider
  // reference ids: decisive when a charge is disputed, noise the rest of the time,
  // and it was sitting between the invoice PDFs the admin came for and the actions
  // below them (BILLING_PAGE_PROPOSAL_2026-07-29 §"What the page does today").
  const [activityOpen, setActivityOpen] = useState(false);
  // Same reasoning as the activity block, one step further: production has zero
  // tenant_webhooks rows across all six organizations, so this block answered
  // nobody's question while taking roughly a third of the page.
  const [developerOpen, setDeveloperOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [planPicker, setPlanPicker] = useState(false);
  // Product line tab inside Plan picker dialog. Default to 'bundle' for
  // cross-sell (matches /pricing default + Upgrade dialog default).
  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<Partial<TenantWebhook> & {
    name?: string; url?: string; event_subscriptions?: string[];
  }>({});
  const [revealedSecret, setRevealedSecret] = useState<{
    name: string; secret: string;
  } | null>(null);
  // Pending webhook delete → drives a ConfirmDialog instead of the
  // forbidden native window.confirm() (svdy-ui §12.6); webhook delete
  // is destructive (stops event deliveries).
  const [pendingDeleteWebhook, setPendingDeleteWebhook] = useState<{
    id: string; name: string;
  } | null>(null);
  const [overLimit, setOverLimit] = useState<{
    plan_id: string; current: number; limit: number; excess: number;
  } | null>(null);
  const [activeWorkers, setActiveWorkers] = useState<{
    id: string; first_name: string; last_name: string; worker_code: string; hire_date?: string | null;
  }[]>([]);
  const [selectedToCancel, setSelectedToCancel] = useState<string[]>([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u, ps, ivs, whs, prof, pm] = await Promise.all([
        subscriptionApi.currentApi.get().catch(() => null),
        subscriptionApi.usageApi.get().catch(() => null),
        subscriptionApi.plansApi.list().catch(() => []),
        subscriptionApi.invoicesApi.list({ limit: 50 }).catch(() => []),
        subscriptionApi.tenantWebhooksApi.list().catch(() => []),
        // Each of these already swallows its own failure so one outage cannot
        // blank the whole page; the billing identity follows the same rule.
        organizationApi.profileApi.get().catch(() => null),
        // Same rule: a provider outage must leave the block without its detail rather
        // than blanking the page a customer opened in order to pay us.
        subscriptionApi.paymentMethodApi.get().catch(() => null),
      ]);
      setSub(s);
      setBillingPreview(prof?.billing_preview ?? null);
      setUsage(u);
      // Single source of truth: canonical filter + tier-order sort.
      // Helpers in components/billing/planMeta.
      setPlans(sortPlansByTier((ps as Plan[]).filter(isCanonicalPlan)));
      setInvoices(ivs as Invoice[]);
      setWebhooks(whs as TenantWebhook[]);
      setCard(pm?.card ?? null);
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Fetch active workers when the over-limit picker opens; defer
  // until then so we don't pay the cost on every billing page load.
  useEffect(() => {
    if (!overLimit) {
      setActiveWorkers([]);
      setSelectedToCancel([]);
      return;
    }
    organizationApi.workersApi.list('active')
      .then((rows) => setActiveWorkers(
        (rows ?? []).map((w: any) => ({
          id: w.id,
          first_name: w.first_name,
          last_name: w.last_name,
          worker_code: w.worker_code,
          hire_date: w.hire_date,
        })),
      ))
      .catch(() => setActiveWorkers([]));
  }, [overLimit]);

  // Same line order as the invoice's own "Bill to" block, so the preview reads as
  // the document rather than as a form.
  // Country-aware, because the first version wrote every address the German way:
  // `[postal_code, city].join(' ')` renders a US address as "19702 Newark,
  // Delaware". On a preview of what the invoice PDF says — a document a finance
  // team matches against a purchase order — an order no local convention
  // recognises is the same defect class the rest of this work removes.
  /**
   * How many of the two billing-identity cards will actually render.
   *
   * Derived from the SAME two conditions the cards themselves use, deliberately — a
   * second copy of "will this card appear" would drift the first time either gate
   * changes, and the symptom would be a half-width card with a hole beside it rather
   * than anything that looks like a bug in this expression.
   */
  const billingIdentityCards =
    (billingPreview ? 1 : 0) + (canUpdatePaymentMethod(sub) ? 1 : 0);

  const billedToLines = useMemo(
    () => (billingPreview ? billedToBlock(billingPreview) : []),
    [billingPreview],
  );

  const currentPlan = sub?.plan;
  const currentPlanName = currentPlan ? getPlanDisplayName(currentPlan, t) : '—';
  const currentPlanDescription = currentPlan ? getPlanDescription(currentPlan, t) : '';
  const scheduledPlan = sub?.scheduled_plan ?? plans.find((plan) => plan.id === sub?.scheduled_plan_id);
  const scheduledPlanName = scheduledPlan
    ? getPlanDisplayName(scheduledPlan, t)
    : t('system:billing.newPlanFallback');
  // Block 1's three sentences. Derived through the pure helpers so the
  // null-vs-zero price distinction and the seat arithmetic are unit-tested rather
  // than inlined in JSX (see subscriptionLifecycle.ts).
  const amount = useMemo(() => planAmount(currentPlan ?? null, sub), [currentPlan, sub]);
  const amountLabel = useMemo(() => {
    if (amount.kind === 'free') return t('system:billing.planAmountFree');
    if (amount.kind === 'quoted') return t('system:billing.planAmountQuoted');
    return t(amount.interval === 'year'
      ? 'system:billing.planAmountYear'
      : 'system:billing.planAmountMonth', {
      amount: amount.amount.toLocaleString(activeLocale(), {
        minimumFractionDigits: 0, maximumFractionDigits: 2,
      }),
      currency: amount.currency,
    });
  }, [amount, t]);

  const periodEndLabel = useMemo(() => {
    if (!sub?.current_period_end) return null;
    const date = new Date(sub.current_period_end).toLocaleDateString(activeLocale());
    return `${t(isEnding(sub) ? 'system:billing.accessUntil' : 'system:billing.renewsOn')} ${date}`;
  }, [sub, t]);

  const seats = useMemo(() => seatSummary(usage), [usage]);
  const seatsLabel = useMemo(() => {
    if (!seats) return null;
    if (seats.kind === 'unmetered') {
      return t('system:billing.seatsUnmetered', { current: seats.current });
    }
    if (seats.kind === 'over') {
      return t('system:billing.seatsOver', {
        current: seats.current, included: seats.included, extra: seats.extra,
      });
    }
    return t('system:billing.seatsWithin', {
      current: seats.current, included: seats.included, remaining: seats.remaining,
    });
  }, [seats, t]);

  // The cost of the NEXT person — the follow-up question to "how many more fit".
  // Currency comes from the subscription; the old copy hardcoded a dollar sign.
  const extraSeatLabel = useMemo(() => {
    if (!usage?.overage_rate || seats?.kind === 'unmetered') return null;
    return t('system:billing.extraSeatRate', {
      rate: usage.overage_rate,
      currency: sub?.billing_currency ?? 'USD',
    });
  }, [usage, seats, sub, t]);

  const visibleInvoices = useMemo(
    () => (showAllInvoices ? invoices : invoices.slice(0, INVOICE_PREVIEW_ROWS)),
    [invoices, showAllInvoices],
  );


  const handleUpgrade = async (
    planId: string, billingInterval: BillingInterval = 'month',
  ) => {
    setBusy(`checkout:${planId}`);
    try {
      // Checkout starts a NEW subscription; change-plan moves an existing one.
      // The question is therefore "can this subscription be changed?", not "has
      // this org ever had one".
      //
      // It used to ask `!sub?.provider_subscription_id`, and that id survives a
      // cancellation on purpose (it is the record of what ended). So a customer
      // who cancelled and came back was routed to change-plan, which refuses any
      // subscription that is not trialing / active / past_due — they could not
      // resubscribe through the product at all. Found while re-subscribing by
      // hand on 2026-07-30.
      //
      // The backend agrees on the same set: `create_checkout` returns 409
      // `subscription_exists` for a live subscription and allows a cancelled one.
      const CHANGEABLE = ['trialing', 'active', 'past_due'];
      const isFresh = !sub?.provider_subscription_id
        || !CHANGEABLE.includes(sub?.status ?? '');
      if (isFresh) {
        const result = await subscriptionApi.checkoutApi.create({
          plan_id: planId,
          return_url: `${window.location.origin}/system/billing?checkout=success`,
          cancel_url: `${window.location.origin}/system/billing?checkout=cancel`,
          billing_interval: billingInterval,
        });
        if (result.checkout_url) {
          window.location.href = result.checkout_url;
        }
        return;
      }
      // Existing subscription: try change-plan first; on 409
      // over-limit prompt the picker.
      try {
        const updated = await subscriptionApi.subscriptionMutationsApi.changePlan({ plan_id: planId });
        setSub(updated);
        setPlanPicker(false);
        const selectedPlan = plans.find((p) => p.id === planId);
        setToast(t('system:billing.toastPlanChanged', {
          plan: selectedPlan
            ? getPlanDisplayName(selectedPlan, t)
            : t('system:billing.newPlanFallback'),
        }));
      } catch (e) {
        if (e instanceof ApiError && e.status === 409
            && (e.detail as any)?.detail?.code === 'subscription_exists') {
          // The API is the boundary, not this component: a stale tab or a
          // bookmarked checkout can still ask for a second subscription. Say what
          // to do instead of surfacing a raw conflict.
          setToast(t('system:billing.toastAlreadySubscribed'));
          setPlanPicker(false);
        } else if (e instanceof ApiError && e.status === 409 && (e.detail as any)?.detail?.error === 'over_worker_limit') {
          const detail = (e.detail as any).detail;
          setOverLimit({
            plan_id: planId,
            current: detail.current_workers,
            limit: detail.new_limit,
            excess: detail.excess,
          });
          setPlanPicker(false);
        } else {
          throw e;
        }
      }
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
    } finally {
      setBusy(null);
    }
  };

  // Enterprise (and any plan with no configured price) is contact-sales, not
  // self-serve checkout — opening a checkout would 400 "no price configured".
  const handleContactSales = (plan: Plan) => {
    const subject = encodeURIComponent(
      t('common:planPicker.enterpriseInquirySubject', {
        plan: getPlanDisplayName(plan, t),
      }),
    );
    window.location.href = `mailto:sales@svdy.com?subject=${subject}`;
  };

  const handleResolveOverLimit = async (    action: 'cancel_workers_now' | 'schedule_for_period_end',
  ) => {
    if (!overLimit) return;
    setBusy('resolve_over_limit');
    try {
      const updated = await subscriptionApi.subscriptionMutationsApi.changePlan({
        plan_id: overLimit.plan_id,
        action,
        worker_ids_to_deactivate: action === 'cancel_workers_now' ? selectedToCancel : undefined,
      });
      setSub(updated);
      setOverLimit(null);
      setSelectedToCancel([]);
      setToast(
        action === 'cancel_workers_now'
          ? t('system:billing.planChangedDeactivated', { count: selectedToCancel.length })
          : t('system:billing.planChangeScheduled'),
      );
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
    } finally {
      setBusy(null);
    }
  };

  const [cancelConfirm, setCancelConfirm] = useState(false);

  const handleCancel = async () => {
    setBusy('cancel');
    try {
      const updated = await subscriptionApi.subscriptionMutationsApi.cancel(true);
      setSub(updated);
      setCancelConfirm(false);
      setToast(t('system:billing.cancelToast'));
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
    } finally {
      setBusy(null);
    }
  };

  /**
   * Open the provider-hosted page for replacing the card on file.
   *
   * Deliberately fetched on CLICK, not on page load: deciding whether to render
   * the button by creating a portal session every time this page opens would put
   * a provider API call on the critical path of a page most admins visit to read
   * an invoice. The card's visibility is gated on `sub.provider_id` instead —
   * the same signal this page already uses to name the provider.
   *
   * A null `portal_url` is the contract's normal answer for "no destination"
   * (no provider customer yet, or the provider is unreachable), so it becomes a
   * plain-language toast rather than an error.
   */
  const handleUpdatePaymentMethod = async () => {
    setBusy('portal');
    try {
      const result = await subscriptionApi.billingPortalApi.create(
        `${window.location.origin}/system/billing`);
      if (result.portal_url) {
        window.location.href = result.portal_url;
        return;
      }
      setToast(t('system:billing.paymentMethod.unavailable'));
    } catch (e) {
      setToast(e instanceof ApiError
        ? translateApiError(e, t)
        : t('system:billing.paymentMethod.unavailable'));
    } finally {
      setBusy(null);
    }
  };

  const handleResume = async () => {
    setBusy('resume');
    try {
      const updated = await subscriptionApi.subscriptionMutationsApi.resume();
      setSub(updated);
      setToast(t('system:billing.resumeToast'));
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
    } finally {
      setBusy(null);
    }
  };

  const handleDownloadCsv = async () => {
    // Fetched through the axios client and saved from a Blob, NOT navigated to.
    // `window.location.href = <relative API path>` is what made this button land
    // on /dashboard: the browser navigated to the SPA's own origin, CloudFront
    // returned index.html for the unknown path, and the router's catch-all
    // redirected. It also sent no Authorization header, since this app
    // authenticates with a bearer token rather than a cookie. See the note on
    // `invoicesApi.exportCsv`.
    setBusy('csv');
    try {
      const blob = await subscriptionApi.invoicesApi.exportCsv();
      // Shared delivery helper: it releases the object URL on the next tick rather than
      // synchronously, which is what keeps Safari from cancelling the download. The
      // local version got the leak right and the timing wrong.
      downloadBlob(blob, `invoices-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
    } finally {
      setBusy(null);
    }
  };

  // ---------- webhook handlers ----------

  const handleSaveWebhook = async () => {
    const name = (editingWebhook.name ?? '').trim();
    const url = (editingWebhook.url ?? '').trim();
    const events = editingWebhook.event_subscriptions ?? [];
    if (!name || !url || events.length === 0) {
      setToast(t('system:billing.webhookValidation'));
      return;
    }
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      setToast(t('system:billing.webhookUrlValidation'));
      return;
    }
    setBusy('save_webhook');
    try {
      const created = await subscriptionApi.tenantWebhooksApi.create({
        name, url, event_subscriptions: events,
      });
      setWebhookDialogOpen(false);
      setEditingWebhook({});
      // Reveal the secret ONCE — backend never returns it again on
      // subsequent reads. Keep it on screen until admin dismisses.
      if (created.secret) {
        setRevealedSecret({ name: created.name, secret: created.secret });
      }
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
    } finally {
      setBusy(null);
    }
  };

  const confirmDeleteWebhook = async () => {
    if (!pendingDeleteWebhook) return;
    const id = pendingDeleteWebhook.id;
    setBusy(`del:${id}`);
    try {
      await subscriptionApi.tenantWebhooksApi.delete(id);
      setToast(t('system:billing.webhookDeleted'));
      setPendingDeleteWebhook(null);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
      throw e; // keep ConfirmDialog open for retry
    } finally {
      setBusy(null);
    }
  };

  const handleTestWebhook = async (id: string) => {
    setBusy(`test:${id}`);
    try {
      const r = await subscriptionApi.tenantWebhooksApi.test(id);
      setToast(r.success ? t('system:billing.testEventDelivered') : t('system:billing.testEventFailed'));
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'));
    } finally {
      setBusy(null);
    }
  };

  // ---------- render ----------
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('system:billing.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('system:billing.subtitleDetail')}
          </Typography>
        </Box>
        <Tooltip title={t("system:billing.refresh")}>
          <IconButton size="small" onClick={fetchAll} disabled={loading}
            sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#fff', '&:hover': { bgcolor: '#f5f5f5' } }}>
            <RefreshOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          {/* Trial / cancel-scheduled banners */}
          {sub?.status === 'trialing' && sub?.trial_ends_at && (
            <Alert severity="info" sx={{ width: '100%', mb: 2 }} icon={<WarningAmberOutlined />}>
              {t('system:billing.trialEndsBanner', {
                date: new Date(sub.trial_ends_at).toLocaleDateString(activeLocale()),
              })}
            </Alert>
          )}
          {sub?.cancel_at_period_end && (
            <Alert
              severity="warning" sx={{ width: '100%', mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={handleResume} disabled={busy === 'resume'}>
                  {busy === 'resume'
                    ? t('system:billing.resuming')
                    : t('system:billing.undoCancel')}
                </Button>
              }
            >
              {t('system:billing.cancelScheduledBanner', {
                date: sub.current_period_end
                  ? new Date(sub.current_period_end).toLocaleDateString(activeLocale())
                  : t('system:billing.periodEnd'),
              })}
            </Alert>
          )}
          {sub?.scheduled_plan_id && sub?.scheduled_at && (
            <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
              {t('system:billing.planScheduledBanner', {
                plan: scheduledPlanName,
                date: new Date(sub.scheduled_at).toLocaleDateString(activeLocale()),
              })}
            </Alert>
          )}

          {/* Phase B (PRODUCT_SUBSCRIPTION_STRATEGY §6) — usage banner.
              Renders an info / warning / error Alert when the tenant
              is approaching or has exceeded any quantitative cap.
              Driven by /v1/subscription/usage which now returns
              (current_workers/max_workers, current_branches/max_branches,
              current_storage_gb/storage_gb). Storage is currently a
              stub (always 0) until the doc-storage aggregator lands;
              the row stays in the banner anyway so the cap row
              renders consistently. */}
          {shouldShowCapacityWarnings(sub) && (
            <UsageBanner
              usage={usage}
              onUpgrade={() => setPlanPicker(true)}
            />
          )}

          {/* 1. Your plan, your team, and the one action that changes them.
              "What am I on" and "how much room do I have" are one question in the
              admin's head, so they are one block. Stated as sentences rather than a
              gauge: the gauge showed a percentage and left the subtraction to the
              reader, while the actual question is "how many more can I add"
              (BILLING_PAGE_PROPOSAL_2026-07-29 §1).

              THE PRIMARY ACTION LIVES HERE, top-right (owner review 2026-08-01).
              It was moved into a "Manage" block on 2026-07-31 and that went too far:
              "I want to change what I'm on" is the same question this card answers
              and the highest-frequency thing on the page, yet the button sat ~1100px
              down, below an 8-row invoice table. On a cancelled tenant it is also
              the CONVERSION action, so it was the least visible of the most
              valuable. The card's right half was empty, so it costs nothing.

              What did NOT come back is the pair. The 07-31 reasoning still holds — an
              irreversible Cancel must not sit beside a routine Change plan with no
              room for a sentence — so exactly ONE button is here, and Cancel is its
              own row further down with its consequence stated. */}
          <Paper elevation={0} sx={{ width: '100%', mb: 2, borderRadius: 2, border: '1px solid rgba(0,0,0,0.12)', overflow: 'hidden' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'flex-start' }}
              gap={2}
              sx={{ p: 2.5 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">{t("system:billing.currentPlan")}</Typography>
                <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 0.25, flexWrap: 'wrap' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {currentPlanName}
                  </Typography>
                  {sub?.status && (
                    <Chip
                      label={t(SUBSCRIPTION_STATUS_KEYS[sub.status] ?? 'system:billing.statuses.unknown')}
                      size="small"
                      color={statusChipColor(sub.status)}
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.65rem', textTransform: 'capitalize' }}
                    />
                  )}
                </Stack>
                {currentPlanDescription && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {currentPlanDescription}
                  </Typography>
                )}

                {/* What it costs, and the one date that matters — labelled by status,
                    so a cancelled plan never claims to renew. */}
                <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 500 }}>
                  {amountLabel}
                  {periodEndLabel ? ` · ${periodEndLabel}` : ''}
                </Typography>

                {/* The headcount answer, stated. */}
                {seatsLabel && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5 }}
                    color={seats?.kind === 'over' ? 'warning.main' : 'text.secondary'}>
                    {seatsLabel}
                    {extraSeatLabel ? ` · ${extraSeatLabel}` : ''}
                  </Typography>
                )}

                {/* What pressing the button will do. In the card BODY, left-aligned
                    with everything else, rather than under the right-aligned button: a
                    right-aligned caption would give this card a second horizontal
                    origin (svdy-ui §15). The proration sentence is the load-bearing
                    half — it is a buyer decision input, so relocating the button must
                    not drop it. */}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  {isEnding(sub)
                    ? t('system:billing.resubscribeHelper')
                    : t('system:billing.changePlanHelper')}
                </Typography>

                {/* Currency, who collects, and when this period began: real facts an
                    admin needs occasionally, so one quiet line rather than four
                    equal-weight columns. */}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
                  {[
                    sub?.billing_currency ?? 'USD',
                    sub?.provider_id ? 'Stripe' : null,
                    sub?.current_period_start
                      ? `${t('system:billing.periodStart')} ${new Date(sub.current_period_start).toLocaleDateString(activeLocale())}`
                      : null,
                  ].filter(Boolean).join(' · ')}
                </Typography>
              </Box>

              <Button
                variant="contained" size="small"
                onClick={() => setPlanPicker(true)}
                sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem', flexShrink: 0,
                      alignSelf: { xs: 'stretch', sm: 'flex-start' },
                      bgcolor: '#0071e3', color: '#fff', boxShadow: 'none',
                      '&:hover': { bgcolor: '#0058b0', boxShadow: 'none' } }}>
                {isEnding(sub)
                  ? t('system:billing.resubscribe')
                  : t('system:billing.changePlan')}
              </Button>
            </Stack>
          </Paper>

          {/* Billed to + Payment method share ONE row.
              Both are the account's billing identity — who the invoice is addressed to,
              and what we charge — so pairing them says they belong together, where
              stacking them read as two unrelated steps. Both also hold narrow content:
              an address block and two sentences, each previously in a full-width card
              whose right two-thirds were empty.

              It buys ~170px above the fold, and that matters on this page specifically:
              the Invoices table is what an admin comes here FOR, and it started roughly
              1150px down on a 13" screen (svdy-ui §2).

              CSS Grid, not MUI `<Grid container>`: this row sits in a Stack beside
              full-width siblings, which is exactly the negative-margin drift of
              svdy-ui §11.1.

              The column count is DERIVED, not fixed at `1fr 1fr`. Both cards are
              conditionally rendered — a Free-plan org has no `provider_id` and gets no
              Payment method card, and an org with no resolved billing preview gets no
              Billed to — so a hardcoded two-column row would leave a half-width card
              with a hole beside it. `auto-fit` was the other candidate and is wrong
              here: at this container width `minmax(360px, 1fr)` resolves to four
              tracks, not two. */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: billingIdentityCards > 1 ? '1fr 1fr' : '1fr',
            },
            gap: 2,
            width: '100%',
            mb: 2,
            // Top-aligned so both cards' first content row shares a baseline
            // (svdy-ui §11.3); `height: 100%` on each card then squares the bottoms.
            alignItems: 'stretch',
          }}>
          {/* Billed to — what the invoice PDF's "Bill to" block will say.
              This screen is where a finance person discovers the problem: they
              download an invoice, find it addressed to a person with no company
              address, and cannot file it. Until 2026-08-04 no screen in the product
              revealed that, so there was nothing to act on.

              Read-only on purpose. The data has ONE editable home (Organization
              Profile); two edit surfaces for one fact is the drift this repo has
              spent the week removing. Values come from the API's resolved
              `billing_preview`, not recomputed here, so this cannot promise
              something the document contradicts. */}
          {billingPreview && (
            <Paper elevation={0} sx={{
              // `height: 100%` so the two cards in this row share a bottom edge.
              // Unequal bottoms in a card row is svdy-ui §11.2's first bug, and the
              // eye reads it as two cards built to different rules.
              height: '100%', borderRadius: 2, border: '1px solid rgba(0,0,0,0.12)',
            }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2.5, py: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                  {t('system:billing.billedTo.title')}
                </Typography>
                <Button
                  size="small"
                  onClick={() => navigate('/organization/profile')}
                  sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
                  {t('system:billing.billedTo.edit')}
                </Button>
              </Stack>
              <Divider />
              <Box sx={{ px: 2.5, py: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('system:billing.billedTo.intro')}
                </Typography>
                {billedToLines.length > 0 ? (
                  billedToLines.map((line, i) => (
                    <Typography key={i} variant="body2"
                      sx={{ fontWeight: i === 0 ? 600 : 400, lineHeight: 1.6 }}>
                      {line}
                    </Typography>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('system:billing.billedTo.empty')}
                  </Typography>
                )}
                {(billingPreview.missing?.length ?? 0) > 0 && (
                  <Alert severity="warning" sx={{ mt: 1.5, fontSize: '0.8125rem' }}>
                    {t('system:billing.billedTo.missing')}
                  </Alert>
                )}
              </Box>
            </Paper>
          )}

          {/* Payment method — the product's only way to change the card.
              Until 2026-08-04 there was none: measured zero payment-method
              affordances on this page and zero portal endpoints in the API, while
              the payment-failed email's primary button pointed HERE and read
              "Update Payment Method". A card that merely expired therefore ended
              in churn, because the only instruction we gave was impossible to
              follow.

              Gated on `sub.provider_id` — the same signal the plan card above
              uses to name the provider — so a Free-plan org with no provider
              customer never sees a button that cannot lead anywhere. */}
          {canUpdatePaymentMethod(sub) && (
            <Paper elevation={0} sx={{
              height: '100%', borderRadius: 2, border: '1px solid rgba(0,0,0,0.12)',
            }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2.5, py: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                  {t('system:billing.paymentMethod.title')}
                </Typography>
                <Button
                  size="small"
                  onClick={handleUpdatePaymentMethod}
                  disabled={busy === 'portal'}
                  sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
                  {busy === 'portal'
                    ? t('system:billing.paymentMethod.opening')
                    : t('system:billing.paymentMethod.action')}
                </Button>
              </Stack>
              <Divider />
              <Box sx={{ px: 2.5, py: 2 }}>
                {/* The card itself — the question this block exists to answer and did
                    not, until 2026-08-10. "Is the card on file the one I think it is,
                    and has it expired?" had no answer anywhere in the product, while a
                    silently expired card is the commonest involuntary-churn cause in
                    SMB SaaS and our only signal was a failed-payment email afterwards.

                    `card === null` is normal (Free plan, a trial before card entry, or
                    a provider we could not reach), and it degrades to exactly what this
                    block showed before rather than to an error. */}
                {card && (
                  <Box sx={{ mb: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {brandLabel(card.brand)}
                      </Typography>
                      {/* Bullets, not the digits alone: •••• 4242 is how the number is
                          printed everywhere else a person checks a card, so it reads as
                          the same fact rather than as a code. */}
                      <Typography variant="body2" sx={{ fontWeight: 600, letterSpacing: '0.08em' }}>
                        •••• {card.last4}
                      </Typography>
                      {expiryLabel(card) && (
                        <Typography variant="caption" color="text.secondary">
                          {t('system:billing.paymentMethod.expires', { value: expiryLabel(card) })}
                        </Typography>
                      )}
                    </Stack>
                    {/* The warning is the whole point. A card that is fine says nothing
                        extra — an always-on status line trains people to ignore it. */}
                    {cardStatus(card, new Date()) === 'expired' && (
                      <Alert severity="error" sx={{ mt: 1, fontSize: '0.8125rem' }}>
                        {t('system:billing.paymentMethod.expired')}
                      </Alert>
                    )}
                    {cardStatus(card, new Date()) === 'expiring' && (
                      <Alert severity="warning" sx={{ mt: 1, fontSize: '0.8125rem' }}>
                        {t('system:billing.paymentMethod.expiringSoon')}
                      </Alert>
                    )}
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary">
                  {t('system:billing.paymentMethod.intro')}
                </Typography>
                {/* Says out loud that the next click leaves this site. An
                    unannounced domain change on a page that then asks for a card
                    number is the shape of a phishing flow, whoever operates it. */}
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                  {t('system:billing.paymentMethod.hostedNote')}
                </Typography>
              </Box>
            </Paper>
          )}

          </Box>

          {/* Invoices */}
          <Paper elevation={0} sx={{ width: '100%', mb: 2, borderRadius: 2, border: '1px solid rgba(0,0,0,0.12)' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2.5, py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                {t('system:billing.invoicesTitle')}
              </Typography>
              <Button
                size="small" startIcon={<FileDownloadOutlined />}
                onClick={handleDownloadCsv}
                disabled={busy === 'csv'}
                sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
                {t('system:billing.exportCsv')}
              </Button>
            </Stack>
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'grey.100' }}>
                  <TableRow>
                    <TableCell>{t("system:billing.invoiceTable.issued")}</TableCell>
                    <TableCell>{t("system:billing.invoiceTable.period")}</TableCell>
                    <TableCell align="right">{t("system:billing.invoiceTable.amount")}</TableCell>
                    <TableCell>{t("common:status.label")}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.length === 0 ? (
                    <TableRow>
                      {/* Two empty states, because they mean opposite things and
                          only one of them is temporary. "No invoices yet." was a
                          dead end for a free tenant, for whom it is the normal and
                          PERMANENT state — the page's second-largest block was a
                          blank for every unpaid customer
                          (BILLING_PAGE_PROPOSAL_2026-07-29 §2). */}
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        {amount.kind === 'free' ? (
                          <Stack alignItems="center" gap={1}>
                            <Typography variant="body2" color="text.secondary">
                              {t('system:billing.noInvoicesFree')}
                            </Typography>
                            <Button
                              size="small" onClick={() => setPlanPicker(true)}
                              sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
                              {t('system:billing.seePlans')}
                            </Button>
                          </Stack>
                        ) : sub?.current_period_end ? (
                          <Typography variant="body2" color="text.secondary">
                            {t('system:billing.firstInvoiceOn', {
                              date: new Date(sub.current_period_end).toLocaleDateString(activeLocale()),
                            })}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('system:billing.noInvoicesYet')}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : visibleInvoices.map((inv) => (
                    <TableRow key={inv.id} hover>
                      <TableCell>{new Date(inv.issued_at).toLocaleDateString(activeLocale())}</TableCell>
                      <TableCell>{inv.period_start} – {inv.period_end}</TableCell>
                      <TableCell align="right">
                        {(inv.amount_cents / 100).toFixed(2)} {inv.currency}
                      </TableCell>
                      <TableCell>
                        {/* A refunded invoice used to read exactly like a paid one:
                            the refund existed only in `billing_events`, which no
                            invoice view reads, so a customer looking for the
                            refund they were promised saw "Paid". The chip states
                            the refund; the amount is on the row already. */}
                        <Chip
                          label={inv.refunded_at
                            ? t(inv.refunded_amount_cents != null
                                && inv.refunded_amount_cents < inv.amount_cents
                                  ? 'system:billing.invoiceStatuses.partiallyRefunded'
                                  : 'system:billing.invoiceStatuses.refunded')
                            : t(INVOICE_STATUS_KEYS[inv.status] ?? 'system:billing.invoiceStatuses.unknown')}
                          size="small"
                          color={inv.refunded_at ? 'default'
                            : inv.status === 'paid' ? 'success'
                            : inv.status === 'past_due' ? 'error' : 'default'}
                          variant="outlined"
                          sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem', textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" gap={0.5} justifyContent="flex-end">
                          {/* DOWNLOAD IS THE PRIMARY ACTION, not "View".
                              The job here is "get the file I attach to an expense
                              claim". `pdf_url` was fetched, stored, exposed in the
                              API response and rendered NOWHERE — the only action
                              opened Stripe's hosted invoice PAGE, which is built as
                              a payment surface and costs an extra hop.

                              Each link is independent, so a row with only one of the
                              two is no longer a dead end. Previously the action was
                              conditional on `hosted_invoice_url` alone: a webhook
                              that delivered only the PDF left the row with no way to
                              obtain the document we were holding. */}
                          {inv.pdf_url && (
                            <Tooltip title={t('system:billing.downloadInvoicePdf')}>
                              <Button size="small" component="a" href={inv.pdf_url}
                                target="_blank" rel="noopener noreferrer"
                                startIcon={<FileDownloadOutlined sx={{ fontSize: 16 }} />}
                                sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0, px: 1 }}>
                                {t('system:billing.downloadPdf')}
                              </Button>
                            </Tooltip>
                          )}
                          {inv.hosted_invoice_url && (
                            <Tooltip title={t("system:billing.viewInvoice")}>
                              <Button size="small" component="a" href={inv.hosted_invoice_url}
                                target="_blank" rel="noopener noreferrer"
                                sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0,
                                      px: 1, color: 'text.secondary' }}>
                                {t('system:billing.view')}
                              </Button>
                            </Tooltip>
                          )}
                          {/* Refunds are operator-only by design; this opens a
                              support case prefilled with the invoice so nobody has
                              to ask which charge is meant. Only offered on rows
                              where a refund is possible: money was paid and none
                              has been returned yet. */}
                          {inv.status === 'paid' && inv.amount_cents > 0 && !inv.refunded_at && (
                            <Button
                              size="small"
                              onClick={() => openSupportCase({
                                category: 'billing',
                                subject: t('system:billing.refundSubject', { id: inv.id }),
                                description: t('system:billing.refundDescription', {
                                  id: inv.id,
                                  amount: (inv.amount_cents / 100).toFixed(2),
                                  currency: inv.currency,
                                  date: new Date(inv.issued_at).toLocaleDateString(activeLocale()),
                                }),
                              })}
                              sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0, px: 1,
                                    color: 'text.secondary' }}>
                              {t('system:billing.requestRefund')}
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {invoices.length > INVOICE_PREVIEW_ROWS && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 1 }}>
                        <Button
                          size="small"
                          onClick={() => setShowAllInvoices((v) => !v)}
                          sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
                          {showAllInvoices
                            ? t('system:billing.showFewer')
                            : t('system:billing.showAllCount', { count: invoices.length })}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Cancel — its own row, and ONLY when there is something to cancel.
              The "Manage" card this replaces existed to hold two actions. With
              Change plan back in the plan card it wrapped a single destructive row
              in a section header, which is chrome for its own sake, and on a
              cancelled tenant it rendered a header with nothing under it.

              THE GUARD IS THE FIX, not the layout. The old condition was
              `!cancel_at_period_end && status !== 'cancelled'`. For an organization
              with no Subscription ROW the backend synthesizes a free-tier one
              carrying `status: 'active'` and `cancel_at_period_end: false`, so both
              halves were true and this error-coloured button was offered to a tenant
              with nothing to cancel — 5 of 6 production organizations on
              2026-08-01, and pressing it 500s. `canCancel` asks what the server
              asks, including provider linkage; see its docstring for why reading
              linkage is right here and is not the 2026-07-30 trap.

              The consequence is stated on the page and not only inside the dialog:
              access continues to the end of the period, then entitlements roll back
              to Free. */}
          {canCancel(sub) && (
            <Paper elevation={0} sx={{ width: '100%', mb: 2, borderRadius: 2, border: '1px solid rgba(0,0,0,0.12)' }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}
                gap={1} sx={{ px: 2.5, py: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {t('system:billing.cancel')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('system:billing.cancelHelper')}
                  </Typography>
                </Box>
                <Button
                  variant="outlined" size="small" color="error"
                  onClick={() => setCancelConfirm(true)} disabled={busy === 'cancel'}
                  sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem', flexShrink: 0 }}>
                  {busy === 'cancel'
                    ? t('system:billing.cancelling')
                    : t('system:billing.cancel')}
                </Button>
              </Stack>
            </Paper>
          )}

          {/* Billing activity (audit trail) — collapsed by default. */}
          <Paper elevation={0} sx={{ width: '100%', mb: 2, borderRadius: 2, border: '1px solid rgba(0,0,0,0.12)' }}>
            <Stack
              direction="row" justifyContent="space-between" alignItems="center"
              onClick={() => setActivityOpen((v) => !v)}
              role="button"
              tabIndex={0}
              aria-expanded={activityOpen}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setActivityOpen((v) => !v);
                }
              }}
              sx={{ px: 2.5, py: 1.5, cursor: 'pointer', userSelect: 'none' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                  {t('system:billing.billingActivity')}
                </Typography>
                {/* Says what it is for, so collapsing it does not turn it into a
                    mystery drawer. */}
                <Typography variant="body2" color="text.secondary">
                  {t('system:billing.activityHelper')}
                </Typography>
              </Box>
              <IconButton size="small" aria-hidden tabIndex={-1}>
                {activityOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            </Stack>
            <Collapse in={activityOpen} unmountOnExit>
              <Divider />
              {/* unmountOnExit: the list fetches on mount, so a collapsed block
                  costs no request until the admin actually opens it. */}
              <BillingActivityList />
            </Collapse>
          </Paper>

          {/* 5. Developer — collapsed.
              Outbound webhooks is a developer surface on a page whose reader runs a
              shop, a site or a clinic. Production has ZERO tenant_webhooks rows
              across all six organizations, so this is not an unloved feature — this
              audience simply does not have the question it answers. Demoted rather
              than removed: it is a real capability with a real, small audience, and
              the tenant router exposes full CRUD (BILLING_PAGE_PROPOSAL §5). */}
          <Paper elevation={0} sx={{ width: '100%', borderRadius: 2, border: '1px solid rgba(0,0,0,0.12)' }}>
            <Stack
              direction="row" justifyContent="space-between" alignItems="center"
              onClick={() => setDeveloperOpen((v) => !v)}
              role="button"
              tabIndex={0}
              aria-expanded={developerOpen}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setDeveloperOpen((v) => !v);
                }
              }}
              sx={{ px: 2.5, py: 1.5, cursor: 'pointer', userSelect: 'none' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                  {t('system:billing.developerTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('system:billing.developerHelper')}
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" gap={1}>
                {webhooks.length > 0 && (
                  <Chip
                    label={webhooks.length} size="small" variant="outlined"
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                )}
                <IconButton size="small" aria-hidden tabIndex={-1}>
                  {developerOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>
              </Stack>
            </Stack>
            <Collapse in={developerOpen} unmountOnExit>
            <Divider />
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2.5, py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                {t('system:billing.webhooksHelper')}
              </Typography>
              <Button
                size="small" variant="outlined"
                onClick={() => { setEditingWebhook({}); setWebhookDialogOpen(true); }}
                sx={{ textTransform: 'none', fontSize: '0.8rem', flexShrink: 0,
                      color: 'text.primary', borderColor: 'rgba(0,0,0,0.2)' }}>
                {t("system:billing.addWebhookButton")}
              </Button>
            </Stack>
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'grey.100' }}>
                  <TableRow>
                    <TableCell>{t("system:billing.webhookTable.name")}</TableCell>
                    <TableCell>{t("system:billing.webhookTable.url")}</TableCell>
                    <TableCell>{t("system:billing.webhookTable.events")}</TableCell>
                    <TableCell>{t("common:status.label")}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {webhooks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        {t('system:billing.noWebhooks')}
                      </TableCell>
                    </TableRow>
                  ) : webhooks.map((w) => (
                    <TableRow key={w.id} hover>
                      <TableCell>{w.name}</TableCell>
                      <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{w.url}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={t('system:billing.webhookEventCount', { count: (w.event_subscriptions ?? []).length })}
                          size="small" variant="outlined"
                          sx={{ borderRadius: 1, height: 18, fontSize: '0.6rem' }} />
                      </TableCell>
                      <TableCell>
                        {w.enabled ? (
                          <Chip label={t('system:billing.enabled')} size="small" color="success" variant="outlined"
                            sx={{ borderRadius: 1, height: 18, fontSize: '0.6rem' }} />
                        ) : (
                          <Chip label={t('system:billing.disabled')} size="small" color="default" variant="outlined"
                            sx={{ borderRadius: 1, height: 18, fontSize: '0.6rem' }} />
                        )}
                        {(w.failure_count ?? 0) > 0 && (
                          <Tooltip title={t('system:billing.webhookFailureTooltip', { count: w.failure_count })}>
                            <Chip label={t('system:billing.webhookFailureCount', { count: w.failure_count })} size="small" color="warning" variant="outlined"
                              sx={{ ml: 0.5, borderRadius: 1, height: 18, fontSize: '0.6rem' }} />
                          </Tooltip>
                        )}
                        {/* Delivery state. The block offered configuration with no
                            feedback: `failure_count` was shown, but whether ANYTHING
                            has ever arrived was not — so an endpoint that has never
                            succeeded looked identical to a healthy one. */}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {w.last_success_at
                            ? t('system:billing.webhookLastSuccess', {
                                when: new Date(w.last_success_at).toLocaleString(activeLocale()),
                              })
                            : t('system:billing.webhookNeverDelivered')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={t("system:billing.sendTestEvent")}>
                          <IconButton size="small" onClick={() => handleTestWebhook(w.id)} disabled={busy === `test:${w.id}`}>
                            <SendOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("system:billing.deleteWebhook")}>
                          <IconButton size="small" color="error" onClick={() => setPendingDeleteWebhook({ id: w.id, name: w.name })} disabled={busy === `del:${w.id}`}>
                            <DeleteOutlineOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </Collapse>
          </Paper>

          {/* Need help with billing? — the contact path the audit found missing,
              now the LAST thing on the page rather than a full-weight card between
              the actions and the collapsed sections (owner review 2026-08-01).
              "I could not work this out myself" belongs at the end, and it was
              competing with the conversion action for attention.

              Demoted in WEIGHT, not removed, and still a real control: a focusable
              button that opens the shared SupportCaseDialog. Borderless because a
              bordered card at the page foot reads as another task. The specific
              path is unchanged and stronger — every refundable invoice row carries
              "Request a refund", prefilled with that invoice, so support never has
              to ask which charge is meant. Refunds stay operator-only: an unbounded
              write against money with no fraud gate, and under card-network rules a
              merchant-initiated reversal whose "who authorised it and why" trail a
              chargeback defence needs (BILLING_PAGE_PROPOSAL_2026-07-29 §4). */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'baseline' }}
            gap={0.75}
            sx={{ width: '100%', mt: 1, mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {t('system:billing.helpTitle')} {t('system:billing.helpBody')}
            </Typography>
            <Button
              variant="text" size="small"
              onClick={() => openSupportCase({ category: 'billing' })}
              sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.75rem',
                    minWidth: 0, p: 0, flexShrink: 0, fontWeight: 500,
                    '&:hover': { background: 'none', textDecoration: 'underline' } }}>
              {t('system:billing.contactUs')}
            </Button>
          </Stack>
        </>
      )}

      {/* Plan picker dialog — pricing card grid */}
      <Dialog open={planPicker} onClose={() => setPlanPicker(false)} maxWidth="lg" fullWidth>
        {/* Header shape matches the TopNav host of the same picker: title on the
            left, a dismiss X on the right. The two hosts had drifted — this one
            put a lone text "Close" in the bottom-right DialogActions, which
            reads as an afterthought on a full-width chooser AND implies
            "discard changes" on a dialog that commits nothing. A plan picker is
            a viewer: nothing to cancel, so the affordance is an X. */}
        <DialogTitle
          sx={{
            fontSize: '1.125rem', fontWeight: 700, pb: 0.5,
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box component="span">{t('common:planPicker.title')}</Box>
          <IconButton
            size="small"
            onClick={() => setPlanPicker(false)}
            aria-label={t('common:actions.close')}
          >
            <CloseOutlined fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* One shared implementation with the TopNav upgrade dialog. These two
              had drifted on title, the yearly sentence, the perMonth separator
              and four CTA labels, and had already caused one production
              incident by drifting on plan sort order (svdy-rules §10.5). */}
          <PlanPicker
            plans={plans}
            currentPlanId={sub?.plan_id ?? null}
            activeWorkers={usage?.current_workers ?? sub?.seat_count ?? null}
            currency={sub?.billing_currency ?? 'USD'}
            busy={!!busy}
            onSelect={(planId, billingInterval) => {
              void handleUpgrade(planId, billingInterval);
            }}
            onContactSales={(plan) => handleContactSales(plan)}
          />
        </DialogContent>
      </Dialog>

      {/* Over-limit downgrade picker (ADR-019 §5) */}
      <Dialog
        open={!!overLimit}
        onClose={() => !busy && setOverLimit(null)}
        maxWidth="md" fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>
          {t('system:billing.overLimit.title')}
        </DialogTitle>
        <DialogContent dividers>
          {overLimit && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('system:billing.overLimit.summary', {
                  current: overLimit.current,
                  limit: overLimit.limit,
                  excess: overLimit.excess,
                })}
              </Alert>

              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {t('system:billing.overLimit.optionDeactivate')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    {t('system:billing.overLimit.deactivateDescription', {
                      count: overLimit.excess,
                    })}
                  </Typography>

                  <Box sx={{ maxHeight: 280, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox"></TableCell>
                          <TableCell>{t("system:billing.workerTable.worker")}</TableCell>
                          <TableCell>{t("system:billing.workerTable.code")}</TableCell>
                          <TableCell>{t("system:billing.workerTable.hireDate")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {activeWorkers.map((w) => {
                          const checked = selectedToCancel.includes(w.id);
                          const atQuota = !checked && selectedToCancel.length >= overLimit.excess;
                          return (
                            <TableRow key={w.id} hover>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  size="small" checked={checked}
                                  disabled={atQuota}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedToCancel([...selectedToCancel, w.id]);
                                    } else {
                                      setSelectedToCancel(selectedToCancel.filter((id) => id !== w.id));
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {w.first_name} {w.last_name}
                                </Typography>
                              </TableCell>
                              <TableCell>{w.worker_code}</TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {w.hire_date ?? '—'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color={selectedToCancel.length === overLimit.excess ? 'success.main' : 'text.secondary'}>
                      {t('system:billing.overLimit.selectedCount', {
                        selected: selectedToCancel.length,
                        required: overLimit.excess,
                      })}
                    </Typography>
                    <Button
                      variant="contained" size="small"
                      onClick={() => handleResolveOverLimit('cancel_workers_now')}
                      disabled={busy === 'resolve_over_limit' || selectedToCancel.length !== overLimit.excess}
                      sx={{ borderRadius: 1, textTransform: 'none' }}>
                      {busy === 'resolve_over_limit'
                        ? t('system:billing.applying')
                        : t('system:billing.overLimit.deactivateAndChange')}
                    </Button>
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {t('system:billing.overLimit.optionSchedule')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    {t('system:billing.overLimit.scheduleDescription', {
                      date: sub?.current_period_end
                        ? new Date(sub.current_period_end).toLocaleDateString(activeLocale())
                        : t('system:billing.periodEnd'),
                    })}
                  </Typography>
                  <Stack direction="row" justifyContent="flex-end">
                    <Button
                      variant="outlined" size="small"
                      onClick={() => handleResolveOverLimit('schedule_for_period_end')}
                      disabled={busy === 'resolve_over_limit'}
                      sx={{ borderRadius: 1, textTransform: 'none' }}>
                      {t('system:billing.overLimit.scheduleChange')}
                    </Button>
                  </Stack>
                </Paper>
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverLimit(null)} disabled={busy === 'resolve_over_limit'} sx={{ textTransform: 'none' }}>
            {t('system:billing.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Webhook create / edit dialog */}
      <Dialog
        open={webhookDialogOpen}
        onClose={() => !busy && setWebhookDialogOpen(false)}
        maxWidth="sm" fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>{t("system:billing.addWebhookTitle")}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label={t("system:billing.webhookForm.nameLabel")} size="small" fullWidth
              value={editingWebhook.name ?? ''}
              onChange={(e) => setEditingWebhook({ ...editingWebhook, name: e.target.value })}
              helperText={t('system:billing.webhookNameHelper')}
            />
            <TextField
              label={t("system:billing.webhookForm.urlLabel")} size="small" fullWidth
              value={editingWebhook.url ?? ''}
              onChange={(e) => setEditingWebhook({ ...editingWebhook, url: e.target.value })}
              placeholder={t('system:billing.webhookPlaceholder')}
              helperText={t('system:billing.webhookHelperText')}
            />
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                {t('system:billing.subscribeToEvents')}
              </Typography>
              <FormGroup row sx={{ rowGap: 0, columnGap: 1.5 }}>
                {WEBHOOK_EVENT_OPTIONS.map((evt) => {
                  const checked = (editingWebhook.event_subscriptions ?? []).includes(evt);
                  return (
                    <FormControlLabel
                      key={evt}
                      control={
                        <Checkbox
                          size="small" checked={checked}
                          onChange={(e) => {
                            const cur = new Set(editingWebhook.event_subscriptions ?? []);
                            if (e.target.checked) cur.add(evt);
                            else cur.delete(evt);
                            setEditingWebhook({
                              ...editingWebhook,
                              event_subscriptions: Array.from(cur),
                            });
                          }}
                        />
                      }
                      label={<Typography variant="caption" sx={{ fontSize: '0.72rem' }}>{t(WEBHOOK_EVENT_KEYS[evt])}</Typography>}
                    />
                  );
                })}
              </FormGroup>
            </Box>
            <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
              {t('system:billing.webhookSigningInfo')}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWebhookDialogOpen(false)} disabled={busy === 'save_webhook'}
            sx={{ textTransform: 'none' }}>{t("system:billing.cancel")}</Button>
          <Button variant="contained" onClick={handleSaveWebhook} disabled={busy === 'save_webhook'}
            sx={{ textTransform: 'none', borderRadius: 1 }}>
            {busy === 'save_webhook'
              ? t('system:billing.creating')
              : t('system:billing.createWebhook')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Secret-reveal dialog (shown once on create) */}
      <Dialog
        open={!!revealedSecret}
        onClose={() => setRevealedSecret(null)}
        maxWidth="sm" fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>
          {t('system:billing.secretDialog.title')}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('system:billing.secretDialog.description')}
          </Alert>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{revealedSecret?.name}</Typography>
          <Box sx={{
            mt: 1, p: 1.5, bgcolor: 'grey.100', borderRadius: 1,
            fontFamily: 'monospace', fontSize: '0.78rem', wordBreak: 'break-all',
          }}>
            {revealedSecret?.secret}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (revealedSecret?.secret) {
                navigator.clipboard?.writeText(revealedSecret.secret);
                setToast(t('system:billing.secretCopied'));
              }
            }}
            sx={{ textTransform: 'none' }}>
            {t('common:actions.copy')}
          </Button>
          <Button variant="contained" onClick={() => setRevealedSecret(null)}
            sx={{ textTransform: 'none', borderRadius: 1 }}>
            {t('system:billing.secretDialog.stored')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel subscription — retention dialog */}
      <Dialog
        open={cancelConfirm}
        onClose={() => !busy && setCancelConfirm(false)}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.125rem' }}>
          {t('system:billing.cancelDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('system:billing.cancelDialog.periodWarning', {
              date: sub?.current_period_end
                ? new Date(sub.current_period_end).toLocaleDateString(activeLocale())
                : t('system:billing.periodEnd'),
            })}
          </Alert>
          <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600 }}>
            {t('system:billing.cancelDialog.whatYouLose')}
          </Typography>
          <Stack spacing={0.75} sx={{ mb: 2 }}>
            {[
              t('system:billing.cancelDialog.workerSeats', {
                count: usage?.included_workers ?? usage?.max_workers ?? '—',
              }),
              t('system:billing.cancelDialog.advancedReports'),
              t('system:billing.cancelDialog.prioritySupport'),
              t('system:billing.cancelDialog.webhooks'),
            ].map((item) => (
              <Stack key={item} direction="row" alignItems="center" spacing={1}>
                <WarningAmberOutlined sx={{ fontSize: 16, color: 'warning.main' }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                  {item}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {t('system:billing.cancelDialog.resumeNote')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setCancelConfirm(false)}
            variant="contained" size="small"
            disabled={busy === 'cancel'}
            sx={{ borderRadius: 1, textTransform: 'none', bgcolor: '#0071e3', '&:hover': { bgcolor: '#0058b0' } }}
          >
            {t('system:billing.cancelDialog.keepPlan')}
          </Button>
          <Button
            onClick={handleCancel}
            variant="outlined" size="small" color="error"
            disabled={busy === 'cancel'}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            {busy === 'cancel'
              ? t('system:billing.cancelling')
              : t('system:billing.cancelDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>

      <ConfirmDialog
        open={!!pendingDeleteWebhook}
        tone="danger"
        title={t('system:billing.webhookDeleteTitle')}
        description={t('system:billing.webhookDeleteDescription')}
        subject={
          pendingDeleteWebhook && (
            <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {pendingDeleteWebhook.name}
            </Box>
          )
        }
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={confirmDeleteWebhook}
        onClose={() => setPendingDeleteWebhook(null)}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Billing activity (audit trail)
// ---------------------------------------------------------------------------

// Event labels are localized via i18n (system:billing.event_*), rendered
// inline in the activity table with a humanized defaultValue fallback.

const BillingActivityList: React.FC = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState<{
    id: string; event_type: string; provider_event_id: string | null; created_at: string | null;
  }[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    subscriptionApi.billingEventsApi?.list()
      .then((data: any) => setEvents(data ?? []))
      // Was `.catch(() => {})`: a failed request rendered "No billing activity yet", which
      // tells an admin tracing a charge that there is nothing to find. Collapsing and
      // reopening the block remounts it (unmountOnExit), which is the retry.
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);

  // Inside the collapsible card's own border: the empty and error states take the same
  // centred anatomy as dashboard widgets (WidgetEmptyState), not a second bordered box.
  if (loading) return <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={20} /></Box>;
  if (failed) return (
    <WidgetEmptyState tone="error" icon={ErrorOutlineOutlined} title={t('system:billing.activityLoadFailed')} />
  );
  if (events.length === 0) return (
    <WidgetEmptyState
      icon={ReceiptLongOutlined}
      title={t('system:billing.noBillingActivity')}
      hint={t('system:billing.noBillingActivityHint')}
    />
  );

  return (
    <TableContainer>
      <Table size="small">
        <TableHead sx={{ bgcolor: 'grey.100' }}>
          <TableRow>
            <TableCell>{t("system:billing.eventTable.event")}</TableCell>
            <TableCell>{t("system:billing.eventTable.date")}</TableCell>
            <TableCell>{t("system:billing.eventTable.providerRef")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(showAll ? events : events.slice(0, ACTIVITY_PREVIEW_ROWS)).map((e) => (
            <TableRow key={e.id} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t(
                    BILLING_EVENT_KEYS[e.event_type] ?? 'system:billing.unknownBillingEvent',
                    { code: e.event_type },
                  )}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  {e.created_at ? new Date(e.created_at).toLocaleString(activeLocale()) : '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'text.secondary' }}>
                  {e.provider_event_id ?? '—'}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
          {events.length > ACTIVITY_PREVIEW_ROWS && (
            <TableRow>
              <TableCell colSpan={3} align="center" sx={{ py: 1 }}>
                <Button
                  size="small"
                  onClick={() => setShowAll((v) => !v)}
                  sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
                  {showAll
                    ? t('system:billing.showFewer')
                    : t('system:billing.showAllCount', { count: events.length })}
                </Button>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SystemBilling;

// ---------------------------------------------------------------------------
// Phase B (PRODUCT_SUBSCRIPTION_STRATEGY §6) — usage banner.
// ---------------------------------------------------------------------------
// Surfaces a single Alert when the tenant is approaching (≥80%) or has
// exceeded (>100%) any quantitative cap exposed by /v1/subscription/usage.
// We compose one severity from the worst row so the user sees one
// banner, not a stack — matches the "single point of friction"
// pattern from PRODUCT_SUBSCRIPTION_STRATEGY §5.2.
//
// Severity ladder:
//   < 80%   -> nothing rendered (silent)
//   80–94%  -> 'info' (advisory: heads-up)
//   95–100% -> 'warning' (urgent: invite an upgrade soon)
//   > 100%  -> 'error'   (over-limit: a feature is or will be blocked)
//
// Storage is currently a stub (current_storage_gb always 0); the
// banner still renders the cap row when storage_gb is set, so the
// shape is consistent — but it never escalates because numerator is 0.

type UsageRow = {
  labelKey: string;
  current: number;
  max: number;
};

const UsageBanner: React.FC<{
  usage: Usage | null;
  onUpgrade: () => void;
}> = ({ usage, onUpgrade }) => {
  const { t } = useTranslation();
  if (!usage) return null;

  // Build one row per quantitative dimension that has a cap. A null
  // cap means "unlimited" → not displayed in the banner.
  const rows: UsageRow[] = [];
  if (usage.max_workers != null) {
    rows.push({
      labelKey: 'system:billing.usage.workers',
      current: usage.current_workers ?? 0,
      max: usage.max_workers,
    });
  }
  if (usage.max_branches != null) {
    rows.push({
      labelKey: 'system:billing.usage.branches',
      current: usage.current_branches ?? 0,
      max: usage.max_branches,
    });
  }

  // Filter rows that warrant any banner (≥ 80%) and pick the worst
  // for the overall severity.
  const triggered = rows
    .map((r) => ({
      ...r,
      pct: r.max > 0 ? Math.round((r.current / r.max) * 100) : 0,
    }))
    .filter((r) => r.pct >= 80);

  if (triggered.length === 0) return null;

  const worstPct = Math.max(...triggered.map((r) => r.pct));
  const severity: 'info' | 'warning' | 'error' =
    worstPct > 100 ? 'error' : worstPct >= 95 ? 'warning' : 'info';

  return (
    <Alert
      severity={severity}
      sx={{ width: '100%', mb: 2 }}
      icon={<WarningAmberOutlined />}
      action={
        <Button color="inherit" size="small" onClick={onUpgrade}>
          {t('system:billing.upgradePlan')}
        </Button>
      }
    >
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {severity === 'error'
          ? t('system:billing.usage.exceededTitle')
          : severity === 'warning'
          ? t('system:billing.usage.nearTitle')
          : t('system:billing.usage.approachingTitle')}
      </Typography>
      <Stack component="ul" sx={{ pl: 2, m: 0 }} spacing={0.25}>
        {triggered.map((r) => (
          <Typography
            key={r.labelKey}
            component="li"
            variant="body2"
            sx={{ fontSize: '0.8125rem' }}
          >
            {t('system:billing.usage.row', {
              current: r.current,
              max: r.max,
              dimension: t(r.labelKey),
              percent: r.pct,
            })}
          </Typography>
        ))}
      </Stack>
    </Alert>
  );
};
